import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { FeatureProvider } from './contexts/FeatureContext';
import { OfflineProvider } from './contexts/OfflineContext';
import { ImpersonationProvider, useImpersonation } from './contexts/ImpersonationContext';
import SessionTimeoutManager from './components/SessionTimeoutManager';
import { Toaster } from './components/ui/sonner';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { WarmupBanner } from './components/WarmupBanner';
import ErrorBoundary from './components/ErrorBoundary';
// v1.1.25 — isolated sync worker for the new "Add Photos + Re-Analyze"
// offline queue (existing-asset path). Self-registers an 'online' listener
// when initExistingAssetSync() is called. Does NOT touch the existing
// OfflineContext sync flow.
import { initExistingAssetSync } from './services/offlineExistingAssetSync';
// v1.1.29 — isolated sync worker for the new facility-wide Observation
// offline queue. Self-registers an 'online' listener when
// initObservationSync() is called. Does NOT touch existing flows.
import { initObservationSync } from './services/offlineObservationSync';
// v1.2.69 — parallel to the observation sync, drains the Architectural
// Capture offline queue when connectivity returns. Own DB, own store,
// own listener — does not touch any existing offline path.
import { initArchitecturalSync } from './services/offlineArchitecturalSync';
// v1.3.17 — Phase 2A of Manual Airplane Mode. Drains AI-heavy
// actions (find-manuals, reanalyze, etc.) that were queued while
// the user had airplane mode on. Own DB, own store, own listener —
// composes over lib/connectivity.js. If this file is deleted, the
// airplane-mode toggle still works; users would just see "network
// offline" toasts on those actions rather than queued replays.
import { initDeferredAiJobsSync } from './services/deferredAiJobsSync';
// v1.3.21 — Focus Mode Phase 3b: offline field-edit drain worker.
import { initOfflineFieldEditSync } from './services/offlineFieldEditSync';
import '@/App.css';

// =============================================================================
// SERVICE WORKER — Unregister stale workers that break file downloads
// =============================================================================
const unregisterServiceWorkers = async () => {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const reg of registrations) {
      await reg.unregister();
      console.log('[PWA] Unregistered service worker:', reg.scope);
    }
  }
};

// =============================================================================
// LOADING COMPONENT
// =============================================================================
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
      <p className="text-slate-600 dark:text-slate-400">Loading...</p>
    </div>
  </div>
);

// =============================================================================
// EAGER LOADED - Core pages needed immediately
// =============================================================================
import LoginPage from './pages/LoginPage';
import FirstRunSetupPage from './pages/FirstRunSetupPage';
import DashboardPage from './pages/DashboardPage';
import Layout from './components/Layout';
import SpatialPageWrapper from './components/spatial/SpatialPageWrapper';

// =============================================================================
// LAZY LOADED - Split into chunks, loaded on demand
// =============================================================================

// Auth pages (rarely revisited after login)
const SetupAccountPage = lazy(() => import('./pages/SetupAccountPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));

// Core FCA pages
const FacilitiesPage = lazy(() => import('./pages/FacilitiesPage'));
const FacilityDetailPage = lazy(() => import('./pages/FacilityDetailPage'));
const AssessmentPage = lazy(() => import('./pages/AssessmentPage'));
const AssessmentsListPage = lazy(() => import('./pages/AssessmentsListPage'));
const ReviewQueuePage = lazy(() => import('./pages/ReviewQueuePage'));
// v1.1.27 — net-new admin-only read-only diagnostics page.
const OfflineDiagnosticsPage = lazy(() => import('./pages/OfflineDiagnosticsPage'));

// Client pages
const ClientsPage = lazy(() => import('./pages/ClientsPage'));
const ClientDetailPage = lazy(() => import('./pages/ClientDetailPage'));
const ProgramsListPage = lazy(() => import('./pages/programs/ProgramsListPage'));
const ProgramDetailPage = lazy(() => import('./pages/programs/ProgramDetailPage'));
const ProgramSharePage = lazy(() => import('./pages/programs/ProgramSharePage'));

// Map pages (heavy - includes Leaflet)
const PortfolioMapPage = lazy(() => import('./pages/PortfolioMapPage'));
const GISMapPage = lazy(() => import('./pages/GISMapPage'));
const WorkOrderMapPage = lazy(() => import('./pages/WorkOrderMapPage'));
const OperationsMapPage = lazy(() => import('./pages/OperationsMapPage'));

// IWMS pages
const WorkOrdersPage = lazy(() => import('./pages/WorkOrdersPage'));
const WorkOrderDetailPage = lazy(() => import('./pages/WorkOrderDetailPage'));
const PMSchedulesPage = lazy(() => import('./pages/PMSchedulesPage'));
const VendorsPage = lazy(() => import('./pages/VendorsPage'));
const WarrantiesPage = lazy(() => import('./pages/WarrantiesPage'));
const LeasesPage = lazy(() => import('./pages/LeasesPage'));
const AssetsPage = lazy(() => import('./pages/AssetsPage'));
const SchedulerPage = lazy(() => import('./pages/SchedulerPage'));
const CalendarPage = lazy(() => import('./pages/CalendarPage'));
// v1.3.36.00 — Procedure Library MVP
const ProceduresListPage   = lazy(() => import('./pages/ProceduresPage').then(m => ({ default: m.ProceduresListPage })));
const ProcedureDetailPage  = lazy(() => import('./pages/ProceduresPage').then(m => ({ default: m.ProcedureDetailPage })));
const ProcedureRunPage     = lazy(() => import('./pages/ProcedureRunPage'));
// v1.3.37.85 — Executable Procedure Runner (Phase 1 slice 5 of the CE
// Data Model spec). DC-gated by procedure_execution_enabled inside
// the page; unlocks the offline-first 16-step mobile runner for
// executions launched from work_packages / job_plans / pm_schedules
// / incidents / iot_alerts / rounds / ad_hoc.
const ProcedureExecutionRunnerPage = lazy(() => import('./pages/ProcedureExecutionRunnerPage'));
const FacilityOneLinePage = lazy(() => import('./pages/FacilityOneLinePage'));
const HoldSignoffLinkPage  = lazy(() => import('./pages/HoldSignoffLinkPage'));
// v1.3.36.10 — Job Plans MVP (Phase 2 of MOP/SOP/PM Spec)
const JobPlansListPage     = lazy(() => import('./pages/JobPlansPage').then(m => ({ default: m.JobPlansListPage })));
const JobPlanDetailPage    = lazy(() => import('./pages/JobPlansPage').then(m => ({ default: m.JobPlanDetailPage })));
// v1.3.36.20 — Work Packages MVP (Phase 3)
const WorkPackagesListPage = lazy(() => import('./pages/WorkPackagesPage').then(m => ({ default: m.WorkPackagesListPage })));
const WorkPackageDetailPage = lazy(() => import('./pages/WorkPackagesPage').then(m => ({ default: m.WorkPackageDetailPage })));
// v1.3.37.50 — CAB (Change Advisory Board) inbox for Phase-5 approvals.
const CABInboxPage = lazy(() => import('./pages/CABInboxPage'));
const CABRequestDetailPage = lazy(() => import('./pages/CABRequestDetailPage'));
const HandoverPacketViewerPage = lazy(() => import('./pages/HandoverPacketViewerPage'));
// v1.3.37.108 — Rounds Runner (mobile-first touch-optimized checklist).
const RoundsRunnerPage = lazy(() => import('./pages/RoundsRunnerPage'));
// v1.3.37.109 — Ops Log Terminal (live-stream + filters + ack).
const OpsLogTerminalPage = lazy(() => import('./pages/OpsLogTerminalPage'));
// v1.3.37.110 — Incident Board (sev1-4 kanban + sparkline + timeline drawer).
const IncidentBoardPage = lazy(() => import('./pages/IncidentBoardPage'));
// v1.3.37.111 — Shift Board (calendar + drag-to-swap crew + roster editor).
const ShiftBoardPage = lazy(() => import('./pages/ShiftBoardPage'));
// v1.3.37.113 — Rounds list (hub tab; opens the mobile Rounds Runner per row).
const RoundsListPage = lazy(() => import('./pages/RoundsListPage'));
// v1.3.37.116 — Phase 3 operator boards for Maintenance Modes, LOTO, Permits.
const MaintenanceModesBoardPage = lazy(() => import('./pages/MaintenanceModesBoardPage'));
const LOTOBoardPage = lazy(() => import('./pages/LOTOBoardPage'));
const WorkPermitsBoardPage = lazy(() => import('./pages/WorkPermitsBoardPage'));
// v1.3.37.60 — IoT Environmental Sensors (Phase-5 Lane B).
const IoTSensorsPage = lazy(() => import('./pages/IoTSensorsPage'));
// v1.3.37.62 — Operate & Utilize hub (BELM launchpad shell).
// v1.3.37.63 — Restructured into three hubs (Operate / Critical Ops / Predict)
// with nested routes rendering each module inside the persistent tab strip.
const OperateHubPage = lazy(() => import('./pages/OperateHubPage'));
const OperateOverviewPage = lazy(() => import('./pages/OperateOverviewPage'));
const CriticalOpsHubPage = lazy(() => import('./pages/CriticalOpsHubPage'));
const CriticalOpsDashboardTab = lazy(() => import('./pages/CriticalOpsHubPage').then(m => ({ default: m.CriticalOpsDashboardTab })));
const PredictHubPage = lazy(() => import('./pages/PredictHubPage'));
const PredictDashboardTab = lazy(() => import('./pages/PredictHubPage').then(m => ({ default: m.PredictDashboardTab })));
const PredictAlertsTab = lazy(() => import('./pages/PredictHubPage').then(m => ({ default: m.PredictAlertsTab })));
const PredictEnergyTab = lazy(() => import('./pages/PredictHubPage').then(m => ({ default: m.PredictEnergyTab })));
// v1.3.36.30 — Blackout Windows admin
const BlackoutsPage        = lazy(() => import('./pages/BlackoutsPage'));
const InternalAssetDetailPage = lazy(() => import('./pages/InternalAssetDetailPage'));
const TechnicianMobilePage = lazy(() => import('./pages/TechnicianMobilePage'));
const SupervisorLiveMapPage = lazy(() => import('./pages/SupervisorLiveMapPage'));
const WOCloseoutHubPage = lazy(() => import('./pages/WOCloseoutHubPage'));
const MessagingPage = lazy(() => import('./pages/MessagingPage'));

// Floor Plans (heavy - includes react-pdf)
const FloorPlansPage = lazy(() => import('./pages/FloorPlansPage'));

// Zones
const ZonesPage = lazy(() => import('./pages/ZonesPage'));

// Capital Projects
const ProjectsListPage = lazy(() => import('./pages/ProjectsListPage'));
const ProjectDetailPage = lazy(() => import('./pages/ProjectDetailPage'));
const RfpEditorPage = lazy(() => import('./pages/RfpEditorPage'));

// Building Inspections
const InspectionsListPage = lazy(() => import('./pages/InspectionsListPage'));
const InspectionDetailPage = lazy(() => import('./pages/InspectionDetailPage'));

// Coming Soon placeholder
const ComingSoonPage = lazy(() => import('./pages/ComingSoonPage'));

// v1.3.35.29 — Vendor capture-only role. Isolated capture surface
// (no Layout, no sidebar) for subcontractors granted photo-capture
// access to specific assessments. Router guard below force-redirects
// role=vendor callers to /vendor/assessments and blocks them from
// every other route.
const VendorAssessmentsPage = lazy(() => import('./pages/VendorAssessmentsPage'));
const VendorCapturePage = lazy(() => import('./pages/VendorCapturePage'));

// Admin pages
const AdminPage = lazy(() => import('./pages/AdminPage'));
const OrganizationPage = lazy(() => import('./pages/OrganizationPage'));
const ChangelogAdminPage = lazy(() => import('./pages/ChangelogAdminPage'));
// v1.2.79 — Founder-only AI Agent Catalog. Read-only inventory of every
// AI agent + AI-invoking service. Gated by founder/super_admin role at
// the API layer.
const FoundersAIAgentsPage = lazy(() => import('./pages/FoundersAIAgentsPage'));
const IWMSAdminPage = lazy(() => import('./pages/IWMSAdminPage'));

// Documentation pages (heavy - includes jspdf)
const DocumentViewerPage = lazy(() => import('./pages/DocumentViewerPage'));

// Analysis pages
const PlanningMapPage = lazy(() => import('./pages/PlanningMapPage'));
const ReportGeneratorPage = lazy(() => import('./pages/ReportGeneratorPage'));
const PresentationViewPage = lazy(() => import('./pages/PresentationViewPage'));
const CompareDashboardPage = lazy(() => import('./pages/CompareDashboardPage'));
const CompareDashboardPublicPage = lazy(() => import('./pages/CompareDashboardPublicPage'));
const InsightsListPage = lazy(() => import('./pages/InsightsListPage'));
const InsightsBuilderPage = lazy(() => import('./pages/InsightsBuilderPage'));
const InsightsPublicPage = lazy(() => import('./pages/InsightsPublicPage'));

// Public pages
const RequestPage = lazy(() => import('./pages/RequestPage'));
const RequestStatusPage = lazy(() => import('./pages/RequestStatusPage'));
const AssetDetailPage = lazy(() => import('./pages/AssetDetailPage'));
const FinanceHubPage = lazy(() => import('./pages/FinanceHubPage'));
const VirtualTwinPage = lazy(() => import('./pages/VirtualTwinPage'));

// Ask FMiQ-One (Natural language portfolio queries)
const AIDashboardPage = lazy(() => import('./pages/AIDashboardPage'));

// Pricing Ingestion (Admin self-service)
const PricingIngestionPage = lazy(() => import('./pages/PricingIngestionPage'));

// Damage Assessments
const TriageAssessmentListPage = lazy(() => import('./pages/triage/TriageAssessmentListPage'));
const NewTriageAssessmentPage = lazy(() => import('./pages/triage/NewTriageAssessmentPage'));
const TriageAssessmentDetailPage = lazy(() => import('./pages/triage/TriageAssessmentDetailPage'));

// Procurement Planner
const ProcurementPlannerPage = lazy(() => import('./pages/ProcurementPlannerPage'));

// Spatial — Drawing Intelligence Engine (integrated module — uses main FCA auth)
const SpatialDashboardPage = lazy(() => import('./pages/spatial/DashboardPage'));
const SpatialDigitizeLandingPage = lazy(() => import('./pages/spatial/DigitizeLandingPage'));
const SpatialDigitizeDashboardPage = lazy(() => import('./pages/spatial/DigitizeDashboardPage'));
const SpatialDigitizeUploadPage = lazy(() => import('./pages/spatial/DigitizeUploadPage'));
const SpatialUploadPage = lazy(() => import('./pages/spatial/UploadPage'));
const SpatialJobsListPage = lazy(() => import('./pages/spatial/JobsListPage'));
const SpatialJobDetailPage = lazy(() => import('./pages/spatial/JobDetailPage'));
const SpatialReviewPage = lazy(() => import('./pages/spatial/ReviewPage'));
const SpatialTemplatesPage = lazy(() => import('./pages/spatial/TemplatesPage'));
const SpatialPortalPage = lazy(() => import('./pages/spatial/PortalPage'));

// Launchpad (role-aware landing page)
const LaunchpadPage = lazy(() => import('./pages/LaunchpadPage'));
const LaunchpadV2Page = lazy(() => import('./pages/LaunchpadV2Page'));

// User Guides — partner & client distributable docs
const UserGuidesPage = lazy(() => import('./pages/UserGuidesPage'));

// FISMA/FedRAMP — Audit Log
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));

// v1.3.30 — API Keys admin (machine-identity service accounts for
// non-interactive batch API access; Aramark-requested)
const ApiKeysAdminPage = lazy(() => import('./pages/ApiKeysAdminPage'));

// Feedback Queue (v1.2.34 — in-app "Report an issue" bubble admin surface)
const FeedbackAdminPage = lazy(() => import('./pages/FeedbackAdminPage'));
const FacilityRemovalRequestsPage = lazy(() => import('./pages/FacilityRemovalRequestsPage'));

// v1.3.35.76 P1 — Trackers Admin (Devices / Mobile Assets / Pairings / Ingest)
const TrackersAdminPage = lazy(() => import('./pages/TrackersAdminPage'));

// v1.3.35.78 P2 — Geofences Admin (draw-on-map polygon / radius editor)
const GeofencesAdminPage = lazy(() => import('./pages/GeofencesAdminPage'));

// Account Security (MFA setup)
const AccountSecurityPage = lazy(() => import('./pages/AccountSecurityPage'));
const UserSettingsPage = lazy(() => import('./pages/UserSettingsPage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
const VerifyEmailChangePage = lazy(() => import('./pages/VerifyEmailChangePage'));

// =============================================================================
// PRIVATE ROUTE WRAPPER
// =============================================================================
const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { effectiveRole } = useImpersonation();
  const location = useLocation();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) return <Navigate to="/login" />;

  // v1.3.35.29 — Vendor role hard-scope. If a vendor lands on any
  // route that isn't part of the /vendor/* capture surface (or
  // the standalone /login flow), bounce them back to their
  // assessments picker. Belt-and-suspenders alongside the
  // per-route VendorRoute guard.
  //
  // v1.3.35.33 — Also fires during "View as Vendor" impersonation.
  // Reads impersonation.effectiveRole first so a super-admin
  // viewing a vendor sees exactly what the vendor sees (redirected
  // to /vendor/assessments) instead of their own founder UI.
  const roleForGate = (effectiveRole || user.role || '').toLowerCase();
  if (roleForGate === 'vendor') {
    const p = location.pathname || '';
    if (!p.startsWith('/vendor/') && p !== '/vendor') {
      return <Navigate to="/vendor/assessments" replace />;
    }
  }

  return children;
};

// v1.3.35.29 — Vendor route guard. Vendors are capture-only users
// who must NEVER see the standard app shell (no sidebar, no nav,
// no dashboards). This wrapper:
//   * 401 -> /login (same as PrivateRoute)
//   * role != 'vendor' -> 404-ish redirect to /home so a curious
//     non-vendor typing /vendor/... doesn't crash into an empty
//     shell.
// The complementary redirect (vendor logged in but trying to hit
// / or /home or /dashboard) is handled by VendorHomeRedirect below.
//
// v1.3.35.33 — Also honours "View as Vendor" impersonation. A
// super-admin who selected a vendor in the impersonation selector
// is allowed onto the /vendor/* surface so they can preview the
// vendor UI without a fresh login.
const VendorRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const { effectiveRole } = useImpersonation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const roleForGate = (effectiveRole || user.role || '').toLowerCase();
  if (roleForGate !== 'vendor') {
    return <Navigate to="/home" replace />;
  }
  return children;
};

// v1.3.35.29 — Homepage redirect when the caller is a vendor.
// Composed at the root '/' route so anyone signed in as vendor
// lands directly on the assessments picker instead of touching
// the standard Launchpad. Non-vendors get the legacy behaviour.
//
// v1.3.35.33 — Also fires during "View as Vendor" impersonation,
// so a super-admin who just picked a vendor from the impersonation
// selector immediately lands on /vendor/assessments instead of
// their own Launchpad.
const VendorHomeRedirect = () => {
  const { user, loading } = useAuth();
  const { effectiveRole } = useImpersonation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  const roleForGate = (effectiveRole || user.role || '').toLowerCase();
  if (roleForGate === 'vendor') {
    return <Navigate to="/vendor/assessments" replace />;
  }
  return <Navigate to={localStorage.getItem('launchpad_version') === 'v2' ? '/home-v2' : '/home'} replace />;
};

// =============================================================================
// LAZY ROUTE WRAPPER - Combines Suspense with Layout
// =============================================================================
const LazyRoute = ({ children, withLayout = true }) => {
  const content = (
    <Suspense fallback={<PageLoader />}>
      {children}
    </Suspense>
  );

  if (withLayout) {
    return (
      <PrivateRoute>
        <Layout>{content}</Layout>
      </PrivateRoute>
    );
  }

  return <PrivateRoute>{content}</PrivateRoute>;
};

// Public route with suspense (no auth)
const PublicLazyRoute = ({ children }) => (
  <Suspense fallback={<PageLoader />}>
    {children}
  </Suspense>
);

// =============================================================================
// APP ROUTES
// =============================================================================
function AppRoutes() {
  return (
    <Routes>
      {/* Public Request Portal Routes (no auth required) */}
      <Route path="/request" element={<PublicLazyRoute><RequestPage /></PublicLazyRoute>} />
      <Route path="/request/:accessCode" element={<PublicLazyRoute><RequestPage /></PublicLazyRoute>} />
      <Route path="/request/status/:trackingNumber" element={<PublicLazyRoute><RequestStatusPage /></PublicLazyRoute>} />
      <Route path="/request/status" element={<PublicLazyRoute><RequestStatusPage /></PublicLazyRoute>} />
      <Route path="/asset/:assetId" element={<PublicLazyRoute><AssetDetailPage /></PublicLazyRoute>} />
      <Route path="/vt/:token" element={<PublicLazyRoute><VirtualTwinPage /></PublicLazyRoute>} />
      <Route path="/programs/share/:token" element={<PublicLazyRoute><ProgramSharePage /></PublicLazyRoute>} />
      
      {/* Auth Routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<FirstRunSetupPage />} />
      <Route path="/setup-account" element={<PublicLazyRoute><SetupAccountPage /></PublicLazyRoute>} />
      <Route path="/reset-password" element={<PublicLazyRoute><ResetPasswordPage /></PublicLazyRoute>} />
      <Route path="/verify-email-change" element={<PublicLazyRoute><VerifyEmailChangePage /></PublicLazyRoute>} />
      
      {/* Launchpad (v1 & v2) — wrapped with standard Layout; sidebar collapsible via Layout toggle */}
      <Route path="/home" element={<LazyRoute withLayout={true}><LaunchpadPage /></LazyRoute>} />
      <Route path="/home-v2" element={<LazyRoute withLayout={true}><LaunchpadV2Page /></LazyRoute>} />

      {/* Dashboard - Full layout with sidebar */}
      <Route
        path="/dashboard"
        element={
          <PrivateRoute>
            <Layout>
              <DashboardPage />
            </Layout>
          </PrivateRoute>
        }
      />

      {/* Default route → preferred Launchpad version (or vendor
          capture-only surface if the caller is role=vendor) */}
      <Route
        path="/"
        element={
          <PrivateRoute>
            <VendorHomeRedirect />
          </PrivateRoute>
        }
      />

      {/* v1.3.35.29 — Vendor capture-only routes. No Layout wrapper —
          vendors NEVER see the standard sidebar / nav. Both routes
          are gated by VendorRoute so non-vendors typing the URL are
          redirected back to /home. */}
      <Route
        path="/vendor/assessments"
        element={
          <VendorRoute>
            <Suspense fallback={<PageLoader />}>
              <VendorAssessmentsPage />
            </Suspense>
          </VendorRoute>
        }
      />
      <Route
        path="/vendor/capture/:assessmentId"
        element={
          <VendorRoute>
            <Suspense fallback={<PageLoader />}>
              <VendorCapturePage />
            </Suspense>
          </VendorRoute>
        }
      />
      
      {/* Client Routes */}
      <Route path="/clients" element={<LazyRoute><ClientsPage /></LazyRoute>} />
      <Route path="/clients/:clientId" element={<LazyRoute><ClientDetailPage /></LazyRoute>} />
      <Route path="/programs" element={<LazyRoute><ProgramsListPage /></LazyRoute>} />
      <Route path="/programs/:programId" element={<LazyRoute><ProgramDetailPage /></LazyRoute>} />
      
      {/* Facility Routes */}
      <Route path="/facilities" element={<LazyRoute><FacilitiesPage /></LazyRoute>} />
      <Route path="/facilities/:facilityId" element={<LazyRoute><FacilityDetailPage /></LazyRoute>} />
      
      {/* Assessment Routes */}
      <Route path="/assessments" element={<LazyRoute><AssessmentsListPage /></LazyRoute>} />
      <Route path="/assessments/:assessmentId" element={<LazyRoute><AssessmentPage /></LazyRoute>} />
      <Route path="/review-queue" element={<LazyRoute><ReviewQueuePage /></LazyRoute>} />
      {/* v1.1.27 — admin-only read-only diagnostics for both offline queues */}
      <Route path="/admin/offline-diagnostics" element={<LazyRoute><OfflineDiagnosticsPage /></LazyRoute>} />
      <Route path="/admin" element={<LazyRoute><AdminPage /></LazyRoute>} />
      
      {/* Damage Assessment Routes */}
      <Route path="/triage" element={<LazyRoute><TriageAssessmentListPage /></LazyRoute>} />
      <Route path="/triage/new" element={<LazyRoute><NewTriageAssessmentPage /></LazyRoute>} />
      <Route path="/triage/:id" element={<LazyRoute><TriageAssessmentDetailPage /></LazyRoute>} />
      
      {/* Procurement Planner */}
      <Route path="/procurement-planner" element={<LazyRoute><ProcurementPlannerPage /></LazyRoute>} />
      
      {/* IWMS Routes */}
      <Route path="/work-orders" element={<LazyRoute><WorkOrdersPage /></LazyRoute>} />
      <Route path="/work-orders/:workOrderId" element={<LazyRoute><WorkOrderDetailPage /></LazyRoute>} />
      <Route path="/messaging" element={<LazyRoute><MessagingPage /></LazyRoute>} />
      <Route path="/pm-schedules" element={<LazyRoute><PMSchedulesPage /></LazyRoute>} />
      <Route path="/vendors" element={<LazyRoute><VendorsPage /></LazyRoute>} />
      <Route path="/warranties" element={<LazyRoute><WarrantiesPage /></LazyRoute>} />
      <Route path="/leases" element={<LazyRoute><LeasesPage /></LazyRoute>} />
      <Route path="/iwms-assets" element={<LazyRoute><AssetsPage /></LazyRoute>} />
      <Route path="/assets/:assetId" element={<LazyRoute><InternalAssetDetailPage /></LazyRoute>} />
      <Route path="/scheduler" element={<LazyRoute><SchedulerPage /></LazyRoute>} />
      <Route path="/calendar" element={<LazyRoute><CalendarPage /></LazyRoute>} />
      {/* v1.3.36.00 — Procedure Library (feature-flag-gated inside the page) */}
      <Route path="/procedures" element={<LazyRoute><ProceduresListPage /></LazyRoute>} />
      <Route path="/procedures/:id" element={<LazyRoute><ProcedureDetailPage /></LazyRoute>} />
      <Route path="/procedures/run/:runId" element={<LazyRoute><ProcedureRunPage /></LazyRoute>} />
      <Route path="/procedures/run/:runId/hold-signoff/:token" element={<LazyRoute><HoldSignoffLinkPage /></LazyRoute>} />
      {/* v1.3.37.85 — Executable Procedure Runner (16 step types,
          IndexedDB queue, live event stream). Feature-gated at
          page level on procedure_execution_enabled. */}
      <Route path="/executions/:id/run" element={<LazyRoute><ProcedureExecutionRunnerPage /></LazyRoute>} />
      {/* v1.3.37.89 — Infrastructure one-line renderer (SVG,
          A/B path colors, click-to-inspect). Gated at page
          level on infra_graph_enabled. */}
      <Route path="/facilities/:facilityId/one-line" element={<LazyRoute><FacilityOneLinePage /></LazyRoute>} />
      {/* v1.3.36.10 — Job Plans (Phase 2) */}
      <Route path="/job-plans" element={<LazyRoute><JobPlansListPage /></LazyRoute>} />
      <Route path="/job-plans/:id" element={<LazyRoute><JobPlanDetailPage /></LazyRoute>} />
      {/* v1.3.36.20 — Work Packages (Phase 3) */}
      <Route path="/work-packages" element={<LazyRoute><WorkPackagesListPage /></LazyRoute>} />
      <Route path="/work-packages/:id" element={<LazyRoute><WorkPackageDetailPage /></LazyRoute>} />
      {/* v1.3.37.50 — CAB (Change Advisory Board) inbox */}
      <Route path="/cab-inbox" element={<LazyRoute><CABInboxPage /></LazyRoute>} />
      {/* v1.3.37.71 — CAB request detail (deep-linkable full page) */}
      <Route path="/cab/requests/:id" element={<LazyRoute><CABRequestDetailPage /></LazyRoute>} />
      {/* v1.3.37.107 — Handover Packet Viewer (Phase 4 killer surface) */}
      <Route path="/handover/:snapshotId" element={<LazyRoute><HandoverPacketViewerPage /></LazyRoute>} />
      <Route path="/handover/shift/:shiftInstanceId" element={<LazyRoute><HandoverPacketViewerPage /></LazyRoute>} />
      {/* v1.3.37.108 — Rounds Runner (mobile-first, on-the-floor checklist) */}
      <Route path="/rounds/runner/:runId" element={<LazyRoute><RoundsRunnerPage /></LazyRoute>} />
      {/* v1.3.37.60 — IoT Environmental Sensors */}
      <Route path="/iot-sensors" element={<LazyRoute><IoTSensorsPage /></LazyRoute>} />
      {/* v1.3.37.63 — Operate hub: nested routes render inside a persistent
          tab strip.  Legacy /operate/:tab keys (work / schedule / change /
          sensors / field from v1.3.37.62) redirect to their new destinations.
          Child routes use withLayout={false} so we don't re-wrap the sidebar
          inside the parent hub's <Outlet />. */}
      <Route path="/operate" element={<LazyRoute><OperateHubPage /></LazyRoute>}>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={<LazyRoute withLayout={false}><OperateOverviewPage /></LazyRoute>} />
        <Route path="work-orders" element={<LazyRoute withLayout={false}><WorkOrdersPage /></LazyRoute>} />
        <Route path="pm-schedules" element={<LazyRoute withLayout={false}><PMSchedulesPage /></LazyRoute>} />
        <Route path="scheduler" element={<LazyRoute withLayout={false}><SchedulerPage /></LazyRoute>} />
        <Route path="calendar" element={<LazyRoute withLayout={false}><CalendarPage /></LazyRoute>} />
        <Route path="operations-map" element={<LazyRoute withLayout={false}><OperationsMapPage /></LazyRoute>} />
      </Route>
      {/* Legacy v1.3.37.62 tab keys — redirect to keep bookmarks alive. */}
      <Route path="/operate/work" element={<Navigate to="/operate/work-orders" replace />} />
      <Route path="/operate/schedule" element={<Navigate to="/operate/scheduler" replace />} />
      <Route path="/operate/change" element={<Navigate to="/critical-ops/cab-inbox" replace />} />
      <Route path="/operate/sensors" element={<Navigate to="/predict/sensors" replace />} />
      <Route path="/operate/field" element={<Navigate to="/technician" replace />} />

      {/* v1.3.37.63 — Critical Operations hub (DC / critical-environment). */}
      <Route path="/critical-ops" element={<LazyRoute><CriticalOpsHubPage /></LazyRoute>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<LazyRoute withLayout={false}><CriticalOpsDashboardTab /></LazyRoute>} />
        <Route path="procedures" element={<LazyRoute withLayout={false}><ProceduresListPage /></LazyRoute>} />
        <Route path="job-plans" element={<LazyRoute withLayout={false}><JobPlansListPage /></LazyRoute>} />
        <Route path="work-packages" element={<LazyRoute withLayout={false}><WorkPackagesListPage /></LazyRoute>} />
        <Route path="cab-inbox" element={<LazyRoute withLayout={false}><CABInboxPage /></LazyRoute>} />
        {/* v1.3.37.113 — Phase 4 Ops surfaces plugged into the hub */}
        <Route path="shifts"    element={<LazyRoute withLayout={false}><ShiftBoardPage /></LazyRoute>} />
        <Route path="ops-log"   element={<LazyRoute withLayout={false}><OpsLogTerminalPage /></LazyRoute>} />
        <Route path="incidents" element={<LazyRoute withLayout={false}><IncidentBoardPage /></LazyRoute>} />
        <Route path="rounds"    element={<LazyRoute withLayout={false}><RoundsListPage /></LazyRoute>} />
        <Route path="handover"  element={<LazyRoute withLayout={false}><HandoverPacketViewerPage /></LazyRoute>} />
        {/* v1.3.37.116 — Phase 3 operator surfaces plugged into the hub */}
        <Route path="maintenance-modes" element={<LazyRoute withLayout={false}><MaintenanceModesBoardPage /></LazyRoute>} />
        <Route path="loto"              element={<LazyRoute withLayout={false}><LOTOBoardPage /></LazyRoute>} />
        <Route path="permits"           element={<LazyRoute withLayout={false}><WorkPermitsBoardPage /></LazyRoute>} />
      </Route>

      {/* v1.3.37.63 — Predict hub (real-time analytics: IoT + energy). */}
      <Route path="/predict" element={<LazyRoute><PredictHubPage /></LazyRoute>}>
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<LazyRoute withLayout={false}><PredictDashboardTab /></LazyRoute>} />
        <Route path="sensors" element={<LazyRoute withLayout={false}><IoTSensorsPage /></LazyRoute>} />
        <Route path="alerts" element={<LazyRoute withLayout={false}><PredictAlertsTab /></LazyRoute>} />
        <Route path="energy" element={<LazyRoute withLayout={false}><PredictEnergyTab /></LazyRoute>} />
      </Route>
      {/* v1.3.36.30 — Blackout Windows admin */}
      <Route path="/blackouts" element={<LazyRoute><BlackoutsPage /></LazyRoute>} />
      <Route path="/technician" element={<LazyRoute><TechnicianMobilePage /></LazyRoute>} />
      <Route path="/wo-closeout" element={<LazyRoute><WOCloseoutHubPage /></LazyRoute>} />
      <Route path="/live-map" element={<Navigate to="/operations-map?view=technicians" replace />} />
      
      {/* Map Routes (Heavy - Leaflet) */}
      <Route path="/operations-map" element={<LazyRoute><OperationsMapPage /></LazyRoute>} />
      <Route path="/portfolio-map" element={<Navigate to="/planning-map" replace />} />
      <Route path="/work-order-map" element={<Navigate to="/operations-map?view=work-orders" replace />} />
      <Route path="/gis-map" element={<Navigate to="/operations-map?view=sites" replace />} />
      {/* Legacy routes preserved for any deep links */}
      <Route path="/portfolio-map-legacy" element={<LazyRoute><PortfolioMapPage /></LazyRoute>} />
      <Route path="/work-order-map-legacy" element={<LazyRoute><WorkOrderMapPage /></LazyRoute>} />
      <Route path="/live-map-legacy" element={<LazyRoute><SupervisorLiveMapPage /></LazyRoute>} />
      <Route path="/gis-map-legacy" element={<LazyRoute><GISMapPage /></LazyRoute>} />
      
      {/* Floor Plans (Heavy - react-pdf) */}
      <Route path="/floor-plans" element={<LazyRoute><FloorPlansPage /></LazyRoute>} />
      
      {/* Zones */}
      <Route path="/zones" element={<LazyRoute><ZonesPage /></LazyRoute>} />
      
      {/* Building Inspections */}
      <Route path="/building-inspections" element={<LazyRoute><InspectionsListPage /></LazyRoute>} />
      <Route path="/building-inspections/:inspectionId" element={<LazyRoute><InspectionDetailPage /></LazyRoute>} />
      <Route path="/capital-projects" element={<LazyRoute><ProjectsListPage /></LazyRoute>} />
      <Route path="/capital-projects/new" element={<LazyRoute><ProjectDetailPage /></LazyRoute>} />
      <Route path="/capital-projects/:projectId" element={<LazyRoute><ProjectDetailPage /></LazyRoute>} />
      <Route path="/capital-projects/:projectId/rfp/new" element={<LazyRoute><RfpEditorPage /></LazyRoute>} />
      <Route path="/capital-projects/:projectId/rfp/:rfpId" element={<LazyRoute><RfpEditorPage /></LazyRoute>} />
      
      {/* Admin Routes */}
      <Route path="/organization" element={<LazyRoute><OrganizationPage /></LazyRoute>} />
      <Route path="/admin/changelog" element={<LazyRoute><ChangelogAdminPage /></LazyRoute>} />
      {/* v1.2.79 — Founder-only AI Agent Catalog */}
      <Route path="/founders/ai-agents" element={<LazyRoute><FoundersAIAgentsPage /></LazyRoute>} />
      {/* v1.3.37.62 — Legacy WMS Launchpad now redirects to Operate & Utilize.
          Preserves existing admin bookmarks; the six-tab hub covers what the
          launchpad did (Work Orders + PM Schedules + Scheduler + Calendar). */}
      <Route path="/iwms-admin" element={<Navigate to="/operate/overview" replace />} />
      <Route path="/iwms-admin-legacy" element={<LazyRoute><IWMSAdminPage /></LazyRoute>} />
      <Route path="/audit-log" element={<LazyRoute><AuditLogPage /></LazyRoute>} />
      <Route path="/admin/api-keys" element={<LazyRoute><ApiKeysAdminPage /></LazyRoute>} />
      <Route path="/admin/feedback" element={<LazyRoute><FeedbackAdminPage /></LazyRoute>} />
      <Route path="/admin/trackers" element={<LazyRoute><TrackersAdminPage /></LazyRoute>} />
      <Route path="/admin/geofences" element={<LazyRoute><GeofencesAdminPage /></LazyRoute>} />
      <Route path="/admin/facility-removal-requests" element={<LazyRoute><FacilityRemovalRequestsPage /></LazyRoute>} />
      <Route path="/admin/removal-requests" element={<LazyRoute><FacilityRemovalRequestsPage /></LazyRoute>} />
      <Route path="/account-security" element={<LazyRoute><AccountSecurityPage /></LazyRoute>} />
      <Route path="/settings" element={<LazyRoute><UserSettingsPage /></LazyRoute>} />
      <Route path="/settings/notifications" element={<LazyRoute><NotificationSettingsPage /></LazyRoute>} />
      {/* Consolidated into System Admin tabs — redirect old standalone routes */}
      <Route path="/health-dashboard" element={<PrivateRoute><Navigate to="/organization?tab=db-health" replace /></PrivateRoute>} />
      <Route path="/platform-config" element={<PrivateRoute><Navigate to="/organization?tab=platform-config" replace /></PrivateRoute>} />
      <Route path="/finance-hub" element={<LazyRoute><FinanceHubPage /></LazyRoute>} />
      <Route path="/ai-dashboard" element={<LazyRoute><AIDashboardPage /></LazyRoute>} />
      <Route path="/pricing-ingestion" element={<LazyRoute><PricingIngestionPage /></LazyRoute>} />
      
      {/* Documentation Routes */}
      <Route path="/system-docs" element={<PrivateRoute><Navigate to="/organization?tab=system-docs" replace /></PrivateRoute>} />
      <Route path="/system-docs/:docSlug" element={<LazyRoute withLayout={false}><DocumentViewerPage /></LazyRoute>} />
      <Route path="/docs/user-guides" element={<LazyRoute><UserGuidesPage /></LazyRoute>} />
      
      {/* Analysis Routes */}
      <Route path="/planning-map" element={<LazyRoute><PlanningMapPage /></LazyRoute>} />
      {/* Legacy redirects */}
      <Route path="/inspection-planner" element={<LazyRoute><PlanningMapPage /></LazyRoute>} />
      <Route path="/regional-analysis" element={<LazyRoute><PlanningMapPage /></LazyRoute>} />
      <Route path="/assessments/:assessmentId/report" element={<LazyRoute><ReportGeneratorPage /></LazyRoute>} />
      <Route path="/assessments/:assessmentId/presentation/:reportId" element={<LazyRoute><PresentationViewPage /></LazyRoute>} />
      <Route path="/assessments/:assessmentId/compare-dashboard" element={<LazyRoute><CompareDashboardPage /></LazyRoute>} />
      <Route path="/compare-dashboard/share/:shareToken" element={<PublicLazyRoute><CompareDashboardPublicPage /></PublicLazyRoute>} />
      <Route path="/insights" element={<LazyRoute><InsightsListPage /></LazyRoute>} />
      <Route path="/insights/:dashboardId" element={<LazyRoute><InsightsBuilderPage /></LazyRoute>} />
      <Route path="/insights/share/:shareToken" element={<PublicLazyRoute><InsightsPublicPage /></PublicLazyRoute>} />
      
      {/* =========================================================== */}
      {/* Spatial — Drawing Intelligence Engine (uses main FCA auth)   */}
      {/* =========================================================== */}
      <Route path="/spatial/portal/:shareToken" element={<PublicLazyRoute><SpatialPortalPage /></PublicLazyRoute>} />
      <Route path="/spatial/dashboard" element={<LazyRoute><SpatialPageWrapper><SpatialDashboardPage /></SpatialPageWrapper></LazyRoute>} />
      <Route path="/spatial/digitize" element={<LazyRoute><SpatialPageWrapper><SpatialDigitizeLandingPage /></SpatialPageWrapper></LazyRoute>} />
      <Route path="/spatial/digitize/dashboard" element={<LazyRoute><SpatialPageWrapper><SpatialDigitizeDashboardPage /></SpatialPageWrapper></LazyRoute>} />
      <Route path="/spatial/digitize/upload" element={<LazyRoute><SpatialPageWrapper><SpatialDigitizeUploadPage /></SpatialPageWrapper></LazyRoute>} />
      <Route path="/spatial/upload" element={<LazyRoute><SpatialPageWrapper><SpatialUploadPage /></SpatialPageWrapper></LazyRoute>} />
      <Route path="/spatial/jobs" element={<LazyRoute><SpatialPageWrapper><SpatialJobsListPage /></SpatialPageWrapper></LazyRoute>} />
      <Route path="/spatial/jobs/:jobId" element={<LazyRoute><SpatialPageWrapper><SpatialJobDetailPage /></SpatialPageWrapper></LazyRoute>} />
      <Route path="/spatial/jobs/:jobId/review" element={<LazyRoute><SpatialPageWrapper><SpatialReviewPage /></SpatialPageWrapper></LazyRoute>} />
      <Route path="/spatial/templates" element={<LazyRoute><SpatialPageWrapper><SpatialTemplatesPage /></SpatialPageWrapper></LazyRoute>} />
    </Routes>
  );
}

// =============================================================================
// APP ENTRY
// =============================================================================
function App() {
  // Unregister stale service workers on mount
  useEffect(() => {
    unregisterServiceWorkers();
  }, []);

  // Request persistent storage on mount (unlocks higher quotas for offline data)
  useEffect(() => {
    if (navigator.storage && navigator.storage.persist) {
      navigator.storage.persist().then(granted => {
        console.log(`[App] Persistent storage ${granted ? 'granted' : 'denied'}`);
      }).catch(() => {});
    }
  }, []);

  // v1.1.25 — initialize the new existing-asset offline sync worker once.
  // Wrapped in try/catch so a worker init failure can never block app boot —
  // existing offline flow keeps working regardless.
  useEffect(() => {
    try {
      initExistingAssetSync();
    } catch (e) {
      console.warn('[App] initExistingAssetSync failed (non-fatal):', e);
    }
  }, []);

  // v1.1.29 — initialize the facility-wide Observation offline sync worker.
  // Same try/catch isolation as v1.1.25.
  useEffect(() => {
    try {
      initObservationSync();
      // v1.2.69 — architectural capture sync bootstrap (isolated,
      // parallel to observation sync — see file header contract).
      try {
        initArchitecturalSync();
      } catch (e) {
        console.warn('[App] initArchitecturalSync failed (non-fatal):', e);
      }
      // v1.3.17 — deferred AI jobs sync bootstrap (Phase 2A of
      // Manual Airplane Mode). Isolated: own DB / store / listener.
      // If it fails to init, only the "queue AI action" replay
      // path is affected; the toggle itself still works.
      try {
        initDeferredAiJobsSync();
      } catch (e) {
        console.warn('[App] initDeferredAiJobsSync failed (non-fatal):', e);
      }
      // v1.3.21 — offline field-edit sync bootstrap (Phase 3b).
      // Isolated: own DB / store / listener. If init fails, only
      // the "queue simple edits" replay path is affected; every
      // other Focus Mode behavior (toggle, cache reads, AI queue)
      // stays fully functional.
      try {
        initOfflineFieldEditSync();
      } catch (e) {
        console.warn('[App] initOfflineFieldEditSync failed (non-fatal):', e);
      }
      // v1.2.97 Phase 4 — Offline snapshot cache auto-refresh.
      // Watches for reconnect / drain signals and refreshes every
      // pinned assessment snapshot in the background. Isolated:
      // consumes only public APIs of the four write-queue modules,
      // never mutates their state. See services/offlineCacheAutoRefresh.js.
      try {
        // Dynamic import so an early bundle-load failure never breaks
        // the observation/architectural sync bootstrap above.
        import('./services/offlineCacheAutoRefresh')
          .then(m => m.initOfflineCacheAutoRefresh())
          .catch((e) => console.warn('[App] initOfflineCacheAutoRefresh import failed (non-fatal):', e));
      } catch (e) {
        console.warn('[App] initOfflineCacheAutoRefresh failed (non-fatal):', e);
      }
    } catch (e) {
      console.warn('[App] initObservationSync failed (non-fatal):', e);
    }
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <FeatureProvider>
          <OfflineProvider>
            <ImpersonationProvider>
              <BrowserRouter>
                <ErrorBoundary message="The application encountered an error. Please try reloading the page.">
                    <ImpersonationBanner />
                    <WarmupBanner />
                    <div className="App">
                      <SessionTimeoutManager />
                      <AppRoutes />
                      <AppToaster />
                    </div>
                </ErrorBoundary>
              </BrowserRouter>
            </ImpersonationProvider>
          </OfflineProvider>
        </FeatureProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

// v1.3.37.153 — vendor screens: toasts at the bottom so they never cover Focus Mode.
function AppToaster() {
  const { pathname } = useLocation();
  return <Toaster position={pathname.startsWith('/vendor/') ? 'bottom-center' : 'top-right'} />;
}

export default App;
